/**
 * Remembers an in-progress call across a page refresh.
 *
 * WebRTC transports cannot survive a reload, so the new page reads this
 * snapshot and rejoins the same call. sessionStorage is per-tab: a refresh
 * keeps it, closing the tab clears it.
 */

export const CALL_RECONNECT_GRACE_MS = 20000;
const STORAGE_KEY = 'chatapp.activeCall';
const SNAPSHOT_TTL_MS = CALL_RECONNECT_GRACE_MS + 10000;

let pageUnloading = false;
const consumedTokens = new Set();

if (typeof window !== 'undefined') {
  const markUnloading = () => {
    pageUnloading = true;
  };
  window.addEventListener('beforeunload', markUnloading);
  window.addEventListener('pagehide', markUnloading);
  // Back-forward cache restores the same JavaScript context.
  window.addEventListener('pageshow', () => {
    pageUnloading = false;
  });
}

export function isPageUnloading() {
  return pageUnloading;
}

export function readActiveCall() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.kind || !data.updatedAt) return null;
    // A ring can sit on screen longer than an in-call reconnect snapshot.
    const maxAge = data.status === 'incoming' ? 120000 : SNAPSHOT_TTL_MS;
    if (Date.now() - data.updatedAt > maxAge) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch (_) {
    return null;
  }
}

export function saveActiveCall(snapshot) {
  if (!snapshot?.kind) return;
  try {
    const prev = readActiveCall();
    if (prev && prev.kind !== snapshot.kind) return;
    const next = {
      ...(prev && prev.kind === snapshot.kind ? prev : {}),
      ...snapshot,
      updatedAt: Date.now(),
    };
    if (next.status !== 'incoming') delete next.offer;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_) {}
}

export function clearActiveCall(kind) {
  try {
    if (kind) {
      const current = readActiveCall();
      if (current && current.kind !== kind) return;
    }
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

/**
 * Claim the snapshot for this page load. A second mounted call UI
 * (header launcher + global listener) must not join the same call twice.
 */
export function consumeActiveCall(kind, userId) {
  const data = readActiveCall();
  if (!data || data.kind !== kind) return null;
  const owner = data.userId?.toString();
  const me = userId?.toString();
  if (owner && me && owner !== me) return null;
  const token = `${data.kind}:${data.callId || data.groupCallId || ''}`;
  if (consumedTokens.has(token)) return null;
  consumedTokens.add(token);
  clearActiveCall(kind);
  return data;
}

export function waitForSocket(socket, timeoutMs = 8000) {
  if (!socket) return Promise.resolve(false);
  if (socket.connected) return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      socket.off('connect', onConnect);
      clearTimeout(timer);
      resolve(ok);
    };
    const onConnect = () => finish(true);
    const timer = setTimeout(() => finish(false), timeoutMs);
    socket.on('connect', onConnect);
  });
}
